'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

// Optical sizes account for each mark's lettering, symbol and visual weight.
// Asset provenance and adaptations: public/logos/customers/README.md.
const CUSTOMERS = [
  { name: 'Lab80', href: 'https://lab80.net', file: 'lab80.svg', width: 112, height: 27 },
  { name: 'voilà', href: 'https://voila.art', file: 'voila.png', width: 106, height: 35 },
  { name: 'Finley', href: 'https://joinfinley.com', file: 'finley.svg', mono: 'finley-mono.svg', width: 29, height: 29, label: true },
  { name: 'Paperstand', href: 'https://paperstand.app', file: 'paperstand.svg', width: 132, height: 28 },
];

/** Static when all four marks fit; a continuous strip on narrower surfaces. */
export default function CustomerLogos({ light = false }: { light?: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    let visible = false;
    const sync = () => setActive(visible && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    });
    observer.observe(element);
    document.addEventListener('visibilitychange', sync);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  return (
    <div ref={root} data-customer-logos data-running={active}
      className={`customer-logos${light ? ' customer-logos--light' : ''}`}>
      <div className="customer-logos__viewport">
        <div className="customer-logos__track">
          {[false, true].map((duplicate) => (
            <div key={String(duplicate)} className={`customer-logos__group${duplicate ? ' customer-logos__duplicate' : ''}`}
              aria-hidden={duplicate || undefined}>
              {CUSTOMERS.map((customer) => (
                <a key={customer.name} href={customer.href} aria-label={customer.name}
                  tabIndex={duplicate ? -1 : undefined} data-trust-logo className="customer-logo">
                  <span className="customer-logo__art" style={{ width: customer.width, height: customer.height }}>
                    <Image src={`/logos/customers/${customer.mono ?? customer.file}`} alt="" fill
                      sizes={`${customer.width}px`} unoptimized className="customer-logo__mono" />
                    {!light && (
                      <Image src={`/logos/customers/${customer.file}`} alt="" fill
                        sizes={`${customer.width}px`} unoptimized className="customer-logo__color" />
                    )}
                  </span>
                  {customer.label && <span className="customer-logo__name">{customer.name}</span>}
                </a>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
